import { theme, Text, SpinnerContainer } from '@alefeducation/ui-elements-library-core'
import { ExclamationCircle, Times } from '@alefeducation/ui-elements-library-icons'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import LogBlock from '../../components/LogBlock'
import useOnScreen from '../../hooks/useOnScreen'
import { CONTENT_TYPES_LIST, DEFAULT_PAGE_SETTING, ENTITY_TYPES, HEADING_COMPONENTS, NameHeading } from '../../utils/constants'
import getLogsForType, { Filters, getLogsParams, getLogsResponse } from '../../xhr/getLogsForType'

const PanelWrapper = styled.div`
  background-color: ${theme.colors.primary[0]};
  width: 100%;
  .headerWrapper {
    background-color: ${theme.colors.interactive[30]};
    .closeWrapper {
      display: flex;
      padding: 16px;
      align-items: flex-start;
      gap: 10px;
      > svg {
        cursor: pointer;
      }
    }
    .headingWrapper {
      display: flex;
      padding: 8px 24px 24px 24px;
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
      align-self: stretch;
    }
  }
  .logsWrapper {
    height: calc(100vh - 142px);
    overflow-y: auto;
    .noLogs {
      text-align: center;
      padding: 24px;
    }
  }
`
const DEFAULT_DATA = {
  totalRows: 0,
  auditLogs: [],
  metadata: {
    name: ''
  }
}

export const Panel = ({ id, type, onClose, panelClass }: { id: string; type: ENTITY_TYPES; onClose?: () => void; panelClass?: string }) => {
  const [pageOptions, setPageOptions] = useState<PageOptions>(DEFAULT_PAGE_SETTING)
  const [error, setError] = useState<string>('')
  const [logsData, setLogsData] = useState<Pick<getLogsResponse, 'totalRows' | 'auditLogs' | 'metadata'>>(DEFAULT_DATA)
  const [loading, setLoading] = useState<boolean>(false)
  const panelRef = useRef()
  const isVisible = useOnScreen(panelRef)

  const isTypeValid = CONTENT_TYPES_LIST[type] !== undefined

  const hasMore = useMemo(() => logsData.auditLogs.length < logsData.totalRows, [logsData])

  const getLogs = async (filters: getLogsParams & Filters) => {
    setLoading(true)
    setError('')
    await getLogsForType({ ...filters })
      .then(res => {
        setLogsData(prev => ({
          totalRows: res.totalRows,
          auditLogs: [...prev.auditLogs, ...res.auditLogs],
          metadata: res.metadata
        }))
        setPageOptions({ pageNumber: res.request.pageNumber, pageSize: res.request.pageSize })
      })
      .catch(e => {
        console.error('Failed to get logs', e)
        setError('Failed to retrieve logs')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    setLogsData(DEFAULT_DATA)
    if (isTypeValid && isVisible) {
      getLogs({
        entityId: id,
        entityType: type,
        pageNumber: DEFAULT_PAGE_SETTING.pageNumber,
        pageSize: DEFAULT_PAGE_SETTING.pageSize
      })
    }
  }, [type, id, isVisible])

  const onClosePanel = () => {
    if (onClose) {
      onClose()
    }
    window.parent.postMessage(
      {
        source: 'audit-logs-fe',
        action: 'closeLog'
      },
      window.location.origin
    )
  }
  if (!isTypeValid) {
    return (
      <div
        style={{
          textAlign: 'center',
          width: '100%'
        }}
      >
        <ExclamationCircle fontSize={25} color="error.100" />
        <br />
        <Text variant="body-large" weight="bold" color="error.100">
          {`Unrecognized content type: ${type}`}
        </Text>
      </div>
    )
  }
  return (
    <PanelWrapper ref={panelRef} className={panelClass} data-testid="panel-wrapper">
      <div className="headerWrapper">
        <div className="closeWrapper">
          <Times color="interactive.140" onClick={onClosePanel} width={14} data-testid="close-side-panel" />
        </div>
        <div className="headingWrapper" data-testid="panel-header-heading">
          {HEADING_COMPONENTS[type] ? HEADING_COMPONENTS[type](logsData.metadata.name) : NameHeading(logsData.metadata.name)}
          <Text variant="body-large" color="interactive.160">
            {CONTENT_TYPES_LIST[type]}
          </Text>
        </div>
      </div>
      <div className="logsWrapper" id="logsWrapperId">
        {!logsData.auditLogs.length && !loading ? (
          <div className="noLogs">
            <Text weight="bold" variant="body-large" color="primary.60">
              No Logs Yet!
            </Text>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={logsData.auditLogs.length}
            style={{
              padding: '24px',
              backgroundColor: theme.colors.primary[0]
            }}
            next={() =>
              getLogs({
                entityId: id,
                entityType: type,
                pageNumber: pageOptions.pageNumber + 1,
                pageSize: pageOptions.pageSize
              })
            }
            scrollableTarget="logsWrapperId"
            hasMore={hasMore}
            loader={<div />}
          >
            {logsData.auditLogs.map((log, index) => (
              <LogBlock data-testid={`${id}-${index}`} key={`${log.auditDate}-${index}`} {...log} isFirstLog={index === 0} />
            ))}
          </InfiniteScroll>
        )}
        {loading ? <SpinnerContainer /> : null}
        {error ? (
          <div
            style={{
              textAlign: 'center'
            }}
          >
            <Text variant="body-large" weight="bold" color="error.100">
              {error}
            </Text>
          </div>
        ) : null}
      </div>
    </PanelWrapper>
  )
}

const SidePanel = () => {
  const [params] = useSearchParams()
  const [targetId, setTargetId] = useState(params.get('id'))
  const [entityType, setEntityType] = useState((params.get('name') || '').toString() as ENTITY_TYPES)

  const panelEventListener = (event: {
    origin: string
    data: {
      source: string
      action: {
        name: string
        id: string
        entityType: string
      }
    }
  }) => {
    if (event.data.source !== undefined && event.data.source === 'audit-logs-embedded-panel-fe') {
      if (event.data.action) {
        switch (event.data.action.name) {
          case 'change-of-id':
            setTargetId(event.data.action.id)
            setEntityType(event.data.action.entityType as ENTITY_TYPES)
            break
          default:
            break
        }
      }
    }
  }

  useEffect(() => {
    window.addEventListener('message', panelEventListener)
    return () => {
      window.removeEventListener('message', panelEventListener)
    }
  }, [])

  return <Panel id={targetId} type={entityType} />
}

export default SidePanel
